import React, { useMemo, useState } from "react";
import { Badge } from "reactstrap";
import {useTheme} from "../context/ThemeContext";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  Plugin,
  ChartEvent,
  ActiveElement,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { ReturnStatsRow, pct } from "./ReturnDistributionStats";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface Props {
  curve: ForwardCurve;
  /**
   * Show the analogs' directional win rate (tooltip line + stat tile). Default true.
   *
   * The game passes false: a win rate over the analogs is exactly the "these predict
   * direction" claim the 2026-07-30 archive audit found does not survive its matched
   * null, and the game deliberately shows only what similar charts *did* (median, spread,
   * extremes) — never a hit rate. The Matcher page keeps it; it's an analytical surface
   * where the number is read alongside the full distribution.
   */
  showWinRate?: boolean;
  /**
   * Draw a shaded 10th-90th percentile band behind the median path. Default false —
   * the dot swarm replaced the band on the Matcher/game surfaces (see the EXPERIMENTAL
   * note below), and this prop brings it back only where it was asked for (Draw a Shape)
   * without changing what any existing consumer renders.
   *
   * p10/p90 don't exist server-side (`_summarize_returns` emits p25/p75 only), but each
   * entry ships its full sorted `returns` array, so the bounds are derived client-side.
   */
  showBand?: boolean;
  /**
   * Cap on how many horizon columns are drawn. The dot plugin draws one canvas arc per
   * analog per horizon, so a long custom forecast window (the backend grid is
   * `forecastWindow * 2` horizons at daily stride — 126 columns for a 3-month horizon)
   * would mean ~12k arcs and a janky chart. Columns are thinned to a UNIFORM stride
   * anchored on the headline horizon: the x axis is a Chart.js category scale, so an
   * uneven stride would render as even spacing and misstate the horizon distance.
   * Default undefined = draw every horizon (unchanged for existing consumers).
   */
  maxColumns?: number;
}

interface MarkerSpec {
  index: number;
  color: string;
  dash: number[];
  label: string;
}

// Concrete colors (NOT CSS variables): canvas strokeStyle/fillStyle can't
// resolve var(--primary), which silently falls back to black — so both the
// vertical bar and its label use this literal to stay identical.
const FORECAST_WINDOW_COLOR = "#0d6efd";  // blue — matches the fan band's primary

/**
 * Draws dashed vertical lines at given x-axis (category) indices, with each
 * label rendered in the chart's TOP PADDING (above the plot area), centered on
 * its own bar — so labels read in left-to-right horizon order, never collide
 * with the fan band, and stay pixel-aligned with the lines. When two labels
 * would overlap (adjacent horizons), the later one moves up a row. A small
 * custom plugin rather than chartjs-plugin-annotation, since category scales +
 * annotation xMin/xMax interact awkwardly.
 */
function buildMarkerPlugin(markers: MarkerSpec[]): Plugin<"line"> {
  return {
    id: "horizonMarkers",
    afterDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      const xScale = scales.x;
      // chart.js's types claim chartArea/scales.x are always defined, but at
      // runtime they're undefined until the first layout pass — guard against
      // that (same false-positive/guard pattern as utils/candlestickPlugin).
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!chartArea || !xScale) return;

      const placed: { left: number; right: number; row: number }[] = [];
      const drawnLineIdx = new Set<number>();
      // Sorted by index so earlier horizons claim row 0 first (left-to-right
      // order). Markers is only ever one entry today (the forecast window),
      // but this stays general enough to stack multiple labels if a second
      // marker is reintroduced later.
      [...markers].sort((a, b) => a.index - b.index).forEach((m) => {
        const x = xScale.getPixelForValue(m.index);
        ctx.save();
        // One bar per horizon: if two markers ever shared a horizon again,
        // draw the first marker's line only and stack both labels above it.
        if (!drawnLineIdx.has(m.index)) {
          drawnLineIdx.add(m.index);
          ctx.beginPath();
          ctx.setLineDash(m.dash);
          ctx.strokeStyle = m.color;
          ctx.lineWidth = 1.5;
          ctx.moveTo(x, chartArea.top);
          ctx.lineTo(x, chartArea.bottom);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        // Label in the top padding, centered on the bar; clamp to the canvas
        // and bump to the row above if it would overlap an already-placed label.
        ctx.font = "11px sans-serif";
        const w = ctx.measureText(m.label).width;
        const lx = Math.max(2, Math.min(x - w / 2, chart.width - w - 2));
        let row = 0;
        for (const p of placed) {
          if (p.row === row && lx < p.right + 8 && lx + w > p.left - 8) row = p.row + 1;
        }
        placed.push({ left: lx, right: lx + w, row });
        ctx.fillStyle = m.color;
        ctx.textAlign = "left";
        ctx.fillText(m.label, lx, chartArea.top - 6 - row * 13);
        ctx.restore();
      });
    },
  };
}

const DOT_RADIUS = 3;
const DOT_DIAMETER = DOT_RADIUS * 2;
// Cap on how wide a single cluster of overlapping dots is allowed to spread —
// keeps a horizon with many tightly-clustered analogs from bleeding sideways
// into its neighboring column.
const MAX_GROUP_WIDTH = 28;

// Per-position (sorted-ascending index -> horizontal slot) offsets for
// fanning k overlapping points out from a shared center. Two things have to
// both be true to avoid a value/x-position correlation:
//
// 1. Offset MAGNITUDE grows outward from the group's median, not its
//    minimum — a plain ascending walk (visit the smallest value first, grow
//    outward from there) puts the narrowest offsets at one tail and the
//    widest at the other, reading as a lopsided "V". Radiating from the
//    median instead means sparse outlier returns (nothing nearby to avoid
//    overlapping) sit near the center line, and the densest cluster
//    (usually near the median) gets the widest spread — real swarm-plot
//    behavior.
// 2. Offset SIGN (which side of center) must NOT be tied to whether a value
//    sits above or below the median. Walking outward from the median
//    naturally alternates between an above-median candidate and a
//    below-median one at every step — if "above" always became "+1" and
//    "below" always "-1", every above-median point would land on the same
//    side as every other, reproducing the same value-correlated diagonal
//    just pivoted at the median instead of the minimum. So which side is
//    "+" flips every step, decoupling side from value direction while still
//    letting magnitude track distance-from-median.
function swarmSlots(k: number): number[] {
  const mid = Math.floor((k - 1) / 2);
  const slots = new Array<number>(k).fill(0);
  let hi = mid + 1;
  let lo = mid - 1;
  let level = 1;
  while (hi < k || lo >= 0) {
    const hiSign = level % 2 === 1 ? 1 : -1;
    if (hi < k) slots[hi++] = level * hiSign;
    if (lo >= 0) slots[lo--] = level * -hiSign;
    level++;
  }
  return slots;
}

/**
 * EXPERIMENTAL: draws every individual analog's realized return as a small dot
 * at its horizon column, jittered sideways so overlapping values fan out
 * instead of stacking into a single vertical line. This is the "single chart"
 * alternative to the shaded percentile band + separate drill-down histogram —
 * dot density IS the distribution, so no click/hover is required to see shape.
 *
 * The jitter is a real swarm layout, grouped by *y-pixel proximity* — NOT by
 * each return's index in the `returns` array. The backend returns `returns`
 * pre-sorted ascending, so a naive index-based jitter (or even a naive
 * left-to-right fan within a proximity group) puts small returns on the left
 * and large ones on the right, drawing a spurious upward-diagonal "trend"
 * that's actually just an artifact of array/value order — see swarmSlots.
 */
function buildAnalogDotsPlugin(entries: ForwardCurveEntry[], colors: { pos: string; neg: string }): Plugin<"line"> {
  return {
    id: "analogDots",
    afterDatasetsDraw(chart) {
      const { ctx, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!xScale || !yScale) return;

      entries.forEach((e, i) => {
        const returns = e.returns;
        if (!returns.length) return;
        const baseX = xScale.getPixelForValue(i);

        // Greedily chain sorted-by-pixel values into groups: any value within
        // one dot-diameter of the last-added item in the current group joins
        // it, otherwise it starts a new group. Points end up ordered by value
        // *within* a group, but since a group only spans ~one dot's height,
        // that ordering is visually imperceptible — it never produces the
        // column-spanning diagonal the naive index-based jitter did.
        const sorted = [...returns].sort((a, b) => a - b).map(r => ({ r, y: yScale.getPixelForValue(r) }));
        const groups: { r: number; y: number }[][] = [];
        for (const item of sorted) {
          const current = groups[groups.length - 1];
          // TS treats array indexing as always-defined without
          // noUncheckedIndexedAccess, but `current` really is undefined on
          // the first iteration (groups starts empty) — same false-positive
          // guard pattern as buildMarkerPlugin's chartArea check above.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (current && Math.abs(item.y - current[current.length - 1].y) < DOT_DIAMETER) {
            current.push(item);
          } else {
            groups.push([item]);
          }
        }

        groups.forEach(group => {
          const k = group.length;
          const slots = swarmSlots(k);
          const maxSlot = Math.max(...slots.map(Math.abs));
          const spacing = maxSlot === 0 ? 0 : Math.min(DOT_DIAMETER, MAX_GROUP_WIDTH / (2 * maxSlot));
          group.forEach((item, idx) => {
            const x = baseX + slots[idx] * spacing;
            ctx.save();
            ctx.beginPath();
            ctx.arc(x, item.y, DOT_RADIUS, 0, Math.PI * 2);
            ctx.fillStyle = item.r >= 0 ? colors.pos : colors.neg;
            ctx.fill();
            ctx.restore();
          });
        });
      });
    },
  };
}

/** English ordinal for a small positive integer ("2nd", "5th", "12th", "22nd"). */
function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const ones = n % 10;
  return `${n}${ones === 1 ? "st" : ones === 2 ? "nd" : ones === 3 ? "rd" : "th"}`;
}

/**
 * Linear-interpolated quantile over an ASCENDING-sorted array. The backend already
 * sorts `returns` ascending (`_summarize_returns`), but callers sort a copy anyway —
 * this must never silently read an unsorted array as if it were sorted.
 */
function quantileOf(sortedAsc: number[], q: number): number | null {
  if (!sortedAsc.length) return null;
  const idx = (sortedAsc.length - 1) * q;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sortedAsc[lo];
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (idx - lo);
}

/**
 * Keep every `stride`-th horizon so the category axis stays a uniform stride (see the
 * `maxColumns` prop). The kept set is anchored on the headline horizon so the forecast-
 * window marker always lands on a real column.
 */
function thinColumns(
  entries: ForwardCurveEntry[],
  horizons: number[],
  headlineHorizon: number,
  maxColumns?: number,
): { entries: ForwardCurveEntry[]; horizons: number[]; stride: number } {
  if (!maxColumns || maxColumns < 2 || entries.length <= maxColumns) {
    return { entries, horizons, stride: 1 };
  }
  const stride = Math.ceil(entries.length / maxColumns);
  const anchorIdx = Math.max(0, horizons.indexOf(headlineHorizon));
  const kept: number[] = [];
  for (let i = anchorIdx % stride; i < entries.length; i += stride) kept.push(i);
  return { entries: kept.map(i => entries[i]), horizons: kept.map(i => horizons[i]), stride };
}

export function ForwardCurveFanChart({ curve, showWinRate = true, showBand = false, maxColumns }: Props) {
  const { horizons: allHorizons, headline_horizon: headlineHorizon, curve: allEntries } = curve;
  const { entries, horizons, stride } = useMemo(
    () => thinColumns(allEntries, allHorizons, headlineHorizon, maxColumns),
    [allEntries, allHorizons, headlineHorizon, maxColumns]
  );
  const [selectedHorizon, setSelectedHorizon] = useState<number>(headlineHorizon);
  const {theme} = useTheme();
  const isDark = theme === "dark";
  // Median + band use the --signature accent (not --primary): this curve is
  // the analog-matching payoff — "what happened historically" — and per the
  // design system --signature is reserved for exactly that, so it reads as
  // visually distinct from the rest of the (blue) UI chrome.
  const themeColors = {
    ticks: isDark ? "#94A3B8" : "#64748B",
    grid: isDark ? "rgba(71, 85, 105, 0.5)" : "rgba(203, 213, 225, 0.5)",
    fanBand: isDark ? "rgba(251, 191, 36, 0.22)" : "rgba(245, 158, 11, 0.14)",
    median: isDark ? "#FBBF24" : "#F59E0B",
    dotPos: isDark ? "rgba(52, 211, 153, 0.65)" : "rgba(5, 150, 105, 0.55)",
    dotNeg: isDark ? "rgba(248, 113, 113, 0.65)" : "rgba(220, 38, 38, 0.55)",
  };

  const selectedEntry = useMemo(
    () => entries.find(e => e.horizon === selectedHorizon) ?? entries.find(e => e.horizon === headlineHorizon) ?? entries[0],
    [entries, selectedHorizon, headlineHorizon]
  );

  // EXPERIMENTAL: dropped the shaded 10th-90th percentile band in favor of
  // plotting every individual analog return as a dot (see buildAnalogDotsPlugin
  // below) — the dot cloud shows the real distribution shape directly instead
  // of an abstract percentile band, so the median line is the only dataset by
  // default. `showBand` re-adds the band as two invisible bounding lines with a
  // fill between them (Chart.js fill: '-1' targets the previous dataset), drawn
  // BEHIND the median (higher `order`) and AFTER it in the array so the median
  // stays dataset index 0 — the tooltip `filter` below keys off that index.
  const bandBounds = useMemo(() => {
    if (!showBand) return null;
    return entries.map(e => {
      const sorted = [...e.returns].sort((a, b) => a - b);
      return { lo: quantileOf(sorted, 0.1), hi: quantileOf(sorted, 0.9) };
    });
  }, [entries, showBand]);

  // entries.find(...) ?? entries.find(...) ?? entries[0] types as non-undefined
  // (TS doesn't track that entries[0] is undefined for an empty array), but at
  // runtime selectedEntry IS undefined when entries is empty. Checking
  // entries.length first makes that the only guard TS needs to see — once
  // it's > 0, entries[0] (and therefore selectedEntry) really is defined.
  if (!entries.length) return null;

  const labels = horizons.map(h => `${h}d`);
  const headlineIdx = horizons.indexOf(headlineHorizon);

  const markers: MarkerSpec[] = [];
  if (headlineIdx >= 0) {
    // headlineHorizon is the same value as the "Forecast Window (days)" field
    // in Advanced Filters — same label here so the two don't read as unrelated
    // concepts.
    markers.push({ index: headlineIdx, color: FORECAST_WINDOW_COLOR, dash: [5, 4], label: `forecast window: ${headlineHorizon}d` });
  }

  const bandDatasets = bandBounds
    ? [
      {
        label: "90th pct",
        data: bandBounds.map(b => b.hi),
        borderColor: "transparent",
        backgroundColor: "transparent",
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.25,
        order: 3,
      },
      {
        label: "10th pct",
        data: bandBounds.map(b => b.lo),
        borderColor: "transparent",
        backgroundColor: themeColors.fanBand,
        borderWidth: 0,
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: "-1",
        tension: 0.25,
        order: 3,
      },
    ]
    : [];

  const chartData = {
    labels,
    datasets: [
      {
        label: "Median Return",
        data: entries.map(e => e.median_return),
        borderColor: themeColors.median,
        backgroundColor: themeColors.median,
        pointRadius: (ctx: { dataIndex: number }) => (horizons[ctx.dataIndex] === selectedHorizon ? 5 : 2.5),
        pointHoverRadius: 6,
        tension: 0.25,
        order: 1,
      },
      ...bandDatasets,
    ],
  };

  // The y-axis scale is normally auto-computed from the datasets Chart.js
  // knows about — but the individual analog dots are drawn by a plugin
  // (buildAnalogDotsPlugin) OUTSIDE the normal dataset/scale pipeline, so
  // auto-scaling only sees the "Median Return" line and can clip real dots
  // (e.g. any negative return) off the visible plot area entirely. Compute
  // the range from every analog return across every horizon instead.
  const allReturns = entries.flatMap(e => e.returns);
  const rangeValues = [0, ...allReturns, ...entries.map(e => e.median_return).filter((v): v is number => v !== null)];
  // Round the bounds outward to whole percentage points before padding, so the
  // axis edges read as clean numbers (e.g. -10%, 14%) instead of the raw
  // computed extremes (-9.5524%, 13.1024%).
  const dataMin = Math.floor(Math.min(...rangeValues));
  const dataMax = Math.ceil(Math.max(...rangeValues));
  const yPad = (dataMax - dataMin) * 0.08 || 1;
  // Round the padded bounds too (yPad is a fraction of the range, so
  // dataMin - yPad isn't a whole number on its own) — otherwise Chart.js's
  // "nice step" tick algorithm still lands boundary labels on values like
  // -9.6% instead of a clean -10%.
  const yMin = Math.floor(dataMin - yPad);
  const yMax = Math.ceil(dataMax + yPad);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    // Top padding is the label band for the marker plugin.
    layout: { padding: { top: 32 } },
    onClick: (_evt: ChartEvent, elements: ActiveElement[]) => {
      if (!elements.length) return;
      const idx = elements[0].index;
      // Chart.js only ever reports indices that exist in the dataset, so
      // horizons[idx] is always defined here — no need for an undefined guard
      // (which TS correctly flags as unreachable given horizons: number[]).
      setSelectedHorizon(horizons[idx]);
    },
    onHover: (evt: ChartEvent, elements: ActiveElement[]) => {
      const target = evt.native?.target as HTMLElement | undefined;
      if (target) target.style.cursor = elements.length ? "pointer" : "default";
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        // Restrict to the "Median Return" dataset (index 0 — the only dataset
        // now that the band datasets are gone). A sparse curve (some horizons
        // have no analogs — see the count > 0 guard in StockChartDisplay) means
        // it can still be `null` at the hovered index, so also require a
        // defined raw value: an empty `items` array reaching `items[0].label`
        // below would throw (which previously corrupted Chart.js's internal
        // tooltip state badly enough to also crash the *next* draw's
        // _drawColorBox with "labelColor is undefined").
        filter: (item: { datasetIndex: number; raw: unknown }) => item.datasetIndex === 0 && item.raw !== null && item.raw !== undefined,
        callbacks: {
          title: (items: { label: string }[]) => (items.length ? `Horizon: ${items[0].label}` : ""),
          label: (item: { dataIndex: number }) => {
            const e = entries[item.dataIndex];
            // entries: ForwardCurveEntry[] types entries[dataIndex] as always
            // defined, but Chart.js dataIndex is just an array position with
            // no compile-time tie to `entries`'s length — keep this guard
            // (it's what stopped the "items[0] is undefined" tooltip crash;
            // see the `filter` comment above for the full story).
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!e) return [];
            const band = bandBounds?.[item.dataIndex];
            return [
              `Median: ${pct(e.median_return)}`,
              ...(band && band.lo !== null && band.hi !== null
                ? [`10th-90th pct: ${pct(band.lo)} to ${pct(band.hi)}`]
                : []),
              ...(showWinRate
                ? [`Win rate: ${e.win_rate !== null ? e.win_rate.toFixed(1) + "%" : "—"}`]
                : []),
              `n = ${e.count}`,
            ];
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: themeColors.ticks as string, font: { size: 10 } },
        grid: { display: false },
      },
      y: {
        min: yMin,
        max: yMax,
        ticks: {
          color: themeColors.ticks as string,
          // yMin/yMax are whole numbers, but Chart.js's own "nice step"
          // selection can still land an interior tick on a fractional value —
          // round display to whole percent as a safety net.
          callback: (v: string | number) => `${Math.round(typeof v === "string" ? parseFloat(v) : v)}%`,
        },
        grid: { color: themeColors.grid as string },
      },
    },
  };

  return (
    <div className="forward-curve-section">
      <h3 className="forward-curve-title">
        Forward Outcome — Every Analog
        <span className="forward-curve-subtitle">
          {selectedEntry.count} historical analogs · each dot is one analog's actual return, median line across horizons
        </span>
      </h3>
      <p className="forward-curve-hint mb-2">
        Each column is a horizon; every dot is one historical analog&apos;s real return at that horizon (red = loss, green = gain).
        {showBand && " The shaded band is the middle 80% of those returns (10th-90th pct)."}
        {stride > 1 && ` Showing every ${ordinal(stride)} horizon so the columns stay readable.`}
        {" "}Click a horizon to see its exact stats below.
      </p>

      {/* Marker labels are drawn by the plugin in the chart's top padding,
          x-aligned with their vertical bars (see buildMarkerPlugin). The
          extra container height accommodates that label band. Capped at
          maxWidth so a handful of horizon points don't stretch into a long,
          flat strip on a wide card — taller and narrower reads as a real
          curve instead. */}
      <div style={{ position: "relative", height: 210, width: "100%", maxWidth: 640 }}>
        <Line
          data={chartData as never}
          options={chartOptions as never}
          plugins={[buildMarkerPlugin(markers), buildAnalogDotsPlugin(entries, { pos: themeColors.dotPos, neg: themeColors.dotNeg })]}
        />
      </div>

      <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
        <span className="forward-curve-horizon-label">
          {selectedHorizon}-day outcome
          {selectedHorizon === headlineHorizon && (
            <Badge color="primary" pill className="forward-curve-badge ms-2">forecast window</Badge>
          )}
        </span>
      </div>
      <div style={{ maxWidth: 640 }}>
        <ReturnStatsRow distribution={selectedEntry} showWinRate={showWinRate} />
      </div>
    </div>
  );
}

export default ForwardCurveFanChart;
